const widthCache = new Map();
let canvas;
let ctx;

function getContext() {
  if (ctx) return ctx;
  if (typeof document === 'undefined') return null;
  canvas = document.createElement('canvas');
  ctx = canvas.getContext('2d');
  return ctx;
}

function makeCacheKey(text, fontSize, fontFamily, fontWeight) {
  return `${fontWeight}|${fontSize}|${fontFamily}|${text}`;
}

export function clearTextMeasureCache() {
  widthCache.clear();
}

export function measureTextWidth(text, options = {}) {
  const content = String(text ?? '');
  if (!content) return 0;

  const fontSize = options.fontSize ?? 12;
  const fontFamily = options.fontFamily ?? 'sans-serif';
  const fontWeight = options.fontWeight ?? 'normal';
  const cacheKey = makeCacheKey(content, fontSize, fontFamily, fontWeight);
  if (widthCache.has(cacheKey)) return widthCache.get(cacheKey);

  const context = getContext();
  if (!context) {
    const heuristic = content.length * fontSize * 0.6;
    widthCache.set(cacheKey, heuristic);
    return heuristic;
  }

  context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const width = context.measureText(content).width;
  widthCache.set(cacheKey, width);
  return width;
}

/**
 * Find the maximum number of characters from `text` that fit within `maxWidthPx`.
 * Uses binary search over measured substrings for accuracy.
 */
export function findCharsForWidth(text, maxWidthPx, options = {}) {
  const s = String(text ?? '');
  if (!s) return 0;
  const fullWidth = measureTextWidth(s, options);
  if (fullWidth <= maxWidthPx) return s.length;
  let lo = 1;
  let hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    const w = measureTextWidth(s.slice(0, mid), options);
    if (w <= maxWidthPx) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Measure the widest label in a list, returning both the width and the text.
 */
export function measureLongestLabel(labels, options = {}) {
  let maxW = 0;
  let maxText = '';
  for (const label of labels) {
    const w = measureTextWidth(String(label ?? ''), options);
    if (w > maxW) { maxW = w; maxText = String(label); }
  }
  return { width: maxW, text: maxText };
}
